/* Animation storyboard
 *   0ms  Acknowledge the requested view; fade the current results out.
 *  80ms  Swap the layout while invisible, then reveal the new results.
 * 240ms  Results are fully visible. Text never translates or scales.
 * Page entry: 320ms opacity / 2px blur reveals, staggered by 40ms (400ms total).
 * Keyboard activation and reduced motion settle immediately.
 */
const TIMING = { exit: 80, enter: 160, arrival: 320, maxStagger: 80 };
const REVEAL = { blur: 2, easing: 'cubic-bezier(0.2, 0, 0, 1)', arrivalEasing: 'ease-in-out' };
const editorialMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const editorialAnimations = new Set();
const settleEditorialLayouts = new Set();
const cancelEditorialAnimations = () => {
  settleEditorialLayouts.forEach((settle) => settle());
  editorialAnimations.forEach((animation) => animation.cancel());
  editorialAnimations.clear();
};
const animateEditorial = (element, frames, options) => {
  const animation = element.animate(frames, options);
  editorialAnimations.add(animation);
  animation.finished.catch(() => {}).finally(() => editorialAnimations.delete(animation));
  return animation;
};
editorialMotion.addEventListener('change', cancelEditorialAnimations);
window.addEventListener('resize', cancelEditorialAnimations);
window.addEventListener('pagehide', cancelEditorialAnimations);

function initializeCategoryRail(rail) {
  if (rail.dataset.ready) return;
  rail.dataset.ready = 'true';
  const nav = rail.querySelector('[data-editorial-categories]');
  const arrows = rail.querySelector('[data-editorial-category-arrows]');
  const buttons = [...arrows.querySelectorAll('button')];
  const rtl = getComputedStyle(nav).direction === 'rtl';
  const update = () => {
    // Measure without the arrow group, so it cannot sustain its own overflow.
    const overflowing = nav.scrollWidth > rail.clientWidth + 1;
    arrows.hidden = !overflowing;
    const max = nav.scrollWidth - nav.clientWidth;
    const position = Math.abs(nav.scrollLeft);
    const start = position > 1;
    const end = position < max - 1;
    nav.dataset.leftOverflow = String(rtl ? end : start);
    nav.dataset.rightOverflow = String(rtl ? start : end);
    buttons[0].setAttribute('aria-disabled', String(!start));
    buttons[1].setAttribute('aria-disabled', String(!end));
  };
  const reveal = (link) => {
    if (!link) return;
    const box = nav.getBoundingClientRect();
    const target = link.getBoundingClientRect();
    const delta = target.left < box.left + 38 ? target.left - box.left - 38
      : target.right > box.right - 38 ? target.right - box.right + 38 : 0;
    nav.scrollBy({ left: delta, behavior: 'instant' });
  };
  const onFocus = (event) => reveal(event.target.closest('a, [data-resource-filter]'));
  const onClick = (event) => {
    const button = event.target.closest('[data-category-direction]');
    if (!button || button.getAttribute('aria-disabled') === 'true') return;
    nav.scrollBy({ left: Number(button.dataset.categoryDirection) * (rtl ? -1 : 1) * Math.max(44, nav.clientWidth - 64), behavior: editorialMotion.matches || event.detail === 0 ? 'instant' : 'smooth' });
  };
  nav.addEventListener('scroll', update, { passive: true });
  nav.addEventListener('focusin', onFocus);
  arrows.addEventListener('click', onClick);
  const observer = new ResizeObserver(update);
  observer.observe(rail);
  update();
  document.fonts?.ready.then(() => { update(); reveal(nav.querySelector('[aria-current="page"]')); });
  const unload = (event) => {
    if (!event.target.contains(rail)) return;
    observer.disconnect();
    nav.removeEventListener('scroll', update);
    nav.removeEventListener('focusin', onFocus);
    arrows.removeEventListener('click', onClick);
    document.removeEventListener('shopify:section:unload', unload);
  };
  document.addEventListener('shopify:section:unload', unload);
}
function initializeEditorial(root = document) {
  root.querySelectorAll('[data-editorial-media]').forEach((media) => {
    if (media.dataset.mediaReady) return;
    const image = media.querySelector('img');
    if (!image) return;
    media.dataset.mediaReady = 'true';
    const settle = () => {
      media.classList.remove('loading-skeleton__surface');
      image.removeEventListener('load', settle);
      image.removeEventListener('error', settle);
    };
    image.addEventListener('load', settle);
    image.addEventListener('error', settle);
    if (image.complete) settle();
    else media.classList.add('loading-skeleton__surface');
  });
  root.querySelectorAll('[data-editorial-index]').forEach((index) => {
    index.querySelectorAll('[data-editorial-category-rail]').forEach(initializeCategoryRail);
    const controls = index.querySelector('[data-editorial-views]');
    const results = index.querySelector('[data-editorial-results]');
    if (!controls || !results || controls.dataset.ready) return;
    controls.dataset.ready = 'true';
    controls.hidden = false;
    let requestedView;
    let revision = 0;
    let transition;
    const settle = () => {
      revision += 1;
      transition?.cancel();
      transition = undefined;
      index.dataset.view = requestedView;
    };
    settleEditorialLayouts.add(settle);
    const setView = async (view, animate = false) => {
      const nextView = view === 'list' ? 'list' : 'grid';
      if (requestedView === nextView) return;
      requestedView = nextView;
      const currentRevision = ++revision;
      const opacity = getComputedStyle(results).opacity;
      // User interaction takes precedence over any unfinished page reveal.
      editorialAnimations.forEach((animation) => animation.cancel());
      editorialAnimations.clear();
      transition?.cancel();
      controls.querySelectorAll('[data-editorial-view]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.editorialView === nextView));
      });
      try { sessionStorage.setItem('sigma-editorial-view', nextView); } catch { /* Storage is optional. */ }
      if (!animate || editorialMotion.matches || typeof results.animate !== 'function') {
        settle();
        return;
      }
      const exit = animateEditorial(results, [{ opacity }, { opacity: 0 }], {
        duration: TIMING.exit, easing: REVEAL.easing, fill: 'forwards',
      });
      transition = exit;
      try { await exit.finished; } catch { return; }
      if (currentRevision !== revision) return;
      // Both layouts stay static. Only change geometry once the old view is invisible.
      index.dataset.view = nextView;
      exit.cancel();
      transition = animateEditorial(results, [
        { opacity: 0, filter: `blur(${REVEAL.blur}px)` },
        { opacity: 1, filter: 'blur(0)' },
      ], { duration: TIMING.enter, easing: REVEAL.easing });
    };
    try { setView(sessionStorage.getItem('sigma-editorial-view')); } catch { setView('grid'); }
    controls.addEventListener('click', (event) => {
      const button = event.target.closest('[data-editorial-view]');
      if (button) setView(button.dataset.editorialView, event.detail > 0);
    });
    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target.contains(index)) {
        settle();
        settleEditorialLayouts.delete(settle);
      }
    });
  });
}
initializeEditorial();
document.addEventListener('shopify:section:load', (event) => initializeEditorial(event.target));

// Internal arrivals use the shared route-entry controller, with the same opt-in
// stagger attributes. Direct visits get the matching reveal without a JS-only gate.
if (!document.documentElement.dataset.routeEntryState && !editorialMotion.matches && !window.Shopify?.designMode) {
  document.querySelectorAll('.editorial [data-route-entry-target]').forEach((target) => {
    const bounds = target.getBoundingClientRect();
    if (bounds.bottom > 0 && bounds.top < window.innerHeight && typeof target.animate === 'function') {
      animateEditorial(target, [{ opacity: 0, filter: `blur(${REVEAL.blur}px)` }, { opacity: 1, filter: 'blur(0)' }], {
        duration: TIMING.arrival,
        delay: Math.min(TIMING.maxStagger, Math.max(0, Number(target.dataset.editorialEntryDelay) || 0)),
        easing: REVEAL.arrivalEasing,
        fill: 'backwards',
      });
    }
  });
}
